import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, Truck, Heart, TrendingDown, TrendingUp, Package, Radio } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { SingleSlab } from './SlabRating';
import { EliteSellerBadge } from './SellerBadges';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { resolveImageUrl } from '../utils/imageUtils';

const getGradeClass = (company, isRaw = false) => {
  if (isRaw) return 'bg-orange-500/80 text-white';
  switch (company?.toUpperCase()) {
    case 'PSA': return 'grade-psa';
    case 'BGS': return 'grade-bgs';
    case 'SGC': return 'grade-sgc';
    default: return 'grade-raw';
  }
};

// Check if card is raw (ungraded)
const isRawCard = (card) => {
  return card.grade_company?.toUpperCase() === 'RAW' || 
         card.item_specifics?.condition === 'Raw' ||
         card.item_specifics?.professional_grader === 'Raw (Ungraded)';
};

// Estimated base shipping rate (lowest typical rate)
const BASE_SHIPPING_ESTIMATE = 5.28;
const PROCESSING_FEE_PERCENT = 0.035;

export const CardListing = ({ card, isWatched = false, onWatchToggle, showPriceDelta = false }) => {
  const [showPricing, setShowPricing] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watched, setWatched] = useState(isWatched);
  const { api, isAuthenticated } = useAuth();

  const handleWatchClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to add to watchlist');
      return;
    }
    
    setWatchLoading(true);
    try {
      if (watched) {
        await api().delete(`/watchlist/${card.id}`);
        setWatched(false);
        toast.success('Removed from watchlist');
      } else {
        await api().post(`/watchlist/${card.id}`);
        setWatched(true);
        toast.success('Added to watchlist');
      }
      if (onWatchToggle) onWatchToggle(card.id, !watched);
    } catch (error) {
      console.error('Watch toggle error:', error);
      toast.error('Failed to update watchlist');
    } finally {
      setWatchLoading(false);
    }
  };

  // Helper to get proper image URL - uses centralized resolver
  const getImageUrl = (imageSource) => {
    return resolveImageUrl(imageSource);
  };

  const imageUrl = getImageUrl(card.images?.[0] || card.image_url);
  
  // Calculate pricing
  const cardPrice = card.price || 0;
  const processingFee = cardPrice * PROCESSING_FEE_PERCENT;
  const shippingEstimate = BASE_SHIPPING_ESTIMATE;
  const estimatedTotal = cardPrice + processingFee + shippingEstimate;
  
  // Price delta (for watchlist)
  const priceDelta = card.price_delta || 0;
  const priceDeltaPercent = card.price_delta_percent || 0;
  const hasPriceDrop = priceDelta < 0;
  const hasPriceIncrease = priceDelta > 0;
  
  // Check if this is a raw card
  const cardIsRaw = isRawCard(card);
  const rawCondition = card.item_specifics?.raw_condition;

  return (
    <Link 
      to={`/cards/${card.id}`}
      className="product-card card-shine group"
      data-testid={`card-listing-${card.id}`}
      onMouseEnter={() => setShowPricing(true)}
      onMouseLeave={() => setShowPricing(false)}
    >
      {/* Image */}
      <div className="card-image-container relative" style={{ zIndex: 10, display: 'block' }}>
        <img 
          src={imageUrl || `https://placehold.co/300x400/1a1a1a/666?text=No+Image`} 
          alt={`${card.player_name} ${card.set_name}`}
          className="w-full h-full"
          style={{ objectFit: 'contain', zIndex: 10, display: 'block' }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/300x400/1a1a1a/666?text=No+Image`;
          }}
        />
        
        {/* Grade/Condition Badge - Dynamic based on Raw vs Graded */}
        {cardIsRaw ? (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-lg text-xs font-bold bg-orange-500/90 text-white flex items-center gap-1" data-testid="raw-badge">
            <Package className="w-3 h-3" />
            Raw {rawCondition ? `- ${rawCondition}` : ''}
          </div>
        ) : (
          <div className={`absolute top-3 left-3 px-2 py-1 rounded-lg text-xs font-bold ${getGradeClass(card.grade_company)}`}>
            {card.grade_company} {card.grade_value || ''}
          </div>
        )}
        
        {/* RC Badge - Rookie Card (works for both raw and graded) */}
        {card.item_specifics?.is_rookie_card && (
          <div className={`absolute top-3 ${cardIsRaw ? 'left-32' : 'left-20'} px-2 py-1 rounded-lg text-xs font-bold bg-emerald-500/90 text-white`} data-testid="rc-badge">
            RC
          </div>
        )}
        
        {/* Serial Number Badge (works for both raw and graded) */}
        {card.item_specifics?.is_serial_numbered && (
          <div className="absolute bottom-12 left-3 px-2 py-1 rounded-lg text-xs font-bold bg-blue-500/90 text-white" data-testid="serial-badge">
            #{card.item_specifics.serial_number || '?'}/{card.item_specifics.total_print_run || '?'}
          </div>
        )}
        
        {/* Price Drop Badge */}
        {showPriceDelta && hasPriceDrop && (
          <div className="absolute bottom-3 left-3 px-2 py-1 rounded-lg text-xs font-bold bg-emerald-500/90 text-white flex items-center gap-1" data-testid="price-drop-badge">
            <TrendingDown className="w-3 h-3" />
            {priceDeltaPercent.toFixed(0)}% OFF
          </div>
        )}
        
        {/* Watch Button */}
        <button
          onClick={handleWatchClick}
          disabled={watchLoading}
          className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 ${
            watched 
              ? 'bg-red-500/90 text-white hover:bg-red-600' 
              : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-white'
          } ${watchLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid={`watch-button-${card.id}`}
        >
          <Heart className={`w-4 h-4 ${watched ? 'fill-current' : ''}`} />
        </button>
        
        {/* LIVE Badge - Shows when seller is live streaming */}
        {card.seller_is_live && (
          <div 
            className="absolute top-12 right-3 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 animate-pulse"
            style={{ backgroundColor: '#00BFFF', color: '#000' }}
            data-testid={`live-badge-${card.id}`}
          >
            <Radio className="w-3 h-3" />
            LIVE
          </div>
        )}
      </div>

      {/* Content - Mobile Optimized */}
      <div className="p-3 sm:p-4 space-y-1.5 sm:space-y-2">
        <h3 className="card-title group-hover:text-blue-400 transition-colors">
          {card.player_name}
        </h3>
        <p className="card-meta truncate">
          {card.year} {card.set_name}
        </p>
        
        {/* Seller Rating with Slab - Only show if seller has REAL rated transactions */}
        {card.seller_total_rated_transactions > 0 && card.seller_rating > 0 && (
          <div className="flex items-center gap-1.5 text-xs">
            <SingleSlab rating={card.seller_rating || 0} size="sm" />
            <span className={`font-medium ${
              card.seller_rating >= 4.5 ? 'text-amber-400' : 
              card.seller_rating > 0 ? 'text-emerald-400' : 'text-zinc-500'
            }`}>{card.seller_rating?.toFixed(1)}</span>
            {card.seller_rating >= 4.5 && card.seller_total_rated_transactions > 0 && (
              <span className="ml-1 hidden sm:inline">
                <EliteSellerBadge size="sm" />
              </span>
            )}
          </div>
        )}

        {/* Pricing - Simplified on Mobile */}
        <div className="pt-2 sm:pt-3 border-t border-zinc-700/50">
          {/* Mobile: Just show price */}
          <div className="sm:hidden">
            <div className="flex items-center justify-between">
              <span className="card-price">${cardPrice.toFixed(2)}</span>
              {showPriceDelta && hasPriceDrop && (
                <span className="text-xs text-emerald-400 flex items-center gap-0.5">
                  <TrendingDown className="w-3 h-3" />
                  {priceDeltaPercent.toFixed(0)}%
                </span>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              +${(processingFee + shippingEstimate).toFixed(2)} fees
            </p>
          </div>
          
          {/* Desktop: Full pricing breakdown */}
          <div className="hidden sm:block space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500">Card Price</span>
            <div className="flex items-center gap-2">
              {showPriceDelta && priceDelta !== 0 && (
                <span className={`text-xs font-medium flex items-center gap-0.5 ${
                  hasPriceDrop ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {hasPriceDrop ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                  {hasPriceDrop ? '-' : '+'}${Math.abs(priceDelta).toFixed(2)}
                </span>
              )}
              <span className={`font-mono text-sm ${hasPriceDrop ? 'text-emerald-400 font-bold' : 'text-white'}`}>
                ${cardPrice.toFixed(2)}
              </span>
            </div>
          </div>
          
          {/* Processing Fee */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              Processing Fee (3.5%)
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-zinc-600 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-zinc-800 border-zinc-700 max-w-[200px]">
                    <p className="text-xs">Secure payment processing fee charged by our payment provider.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
            <span className="font-mono text-xs text-zinc-400">${processingFee.toFixed(2)}</span>
          </div>
          
          {/* Shipping Estimate */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Truck className="w-3 h-3" />
              Shipping (est.)
            </span>
            <span className="font-mono text-xs text-zinc-400">${shippingEstimate.toFixed(2)}</span>
          </div>
          
          {/* Estimated Total */}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-700/30">
            <span className="text-xs font-medium text-zinc-300">Est. Total</span>
            <span className="font-mono font-bold text-emerald-400">${estimatedTotal.toFixed(2)}</span>
          </div>
          </div>
        </div>

        {/* Zero Commission Badge - Hidden on mobile */}
        <div className="pt-2 hidden sm:block">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 cursor-help">
                  <span className="text-[10px] font-medium text-emerald-400">$0 COMMISSION</span>
                  <Info className="w-2.5 h-2.5 text-emerald-400" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-zinc-800 border-zinc-700 max-w-[220px]">
                <p className="text-xs text-zinc-300">
                  <span className="font-semibold text-emerald-400">Slab Society charges $0 commission.</span>
                  {' '}You only pay for shipping and secure payment processing.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </Link>
  );
};

export default CardListing;
